/**
 * Линейная программа: длинные истории с затухающими подсказками,
 * карточки правил и дриллы одного урока.
 *
 * Урок генерируется один раз на навык и лежит в `CachedLesson.payload`;
 * эта схема проверяет его и на записи (ответ модели), и на чтении (кэш).
 * Обе ветки стратегии живут в одном уроке: INDUCTIVE показывает историю
 * до карточек правил, DEDUCTIVE — после. Порядок выбирается в рантайме,
 * поэтому на ученика ничего не генерируется.
 *
 * Затухающие подсказки зависят от ступени (`FADING_RULES`): история
 * постепенно «заполняется» английским — доля английских слов в тексте,
 * каким его видит ученик:
 *   A1 — 20%: русский текст, английские фразы-чанки и простые реплики;
 *   A2 — 40%: целые английские предложения среди русских;
 *   B1 — 50%: языки чередуются поровну;
 *   B2 — 70%: английский текст с русскими вставками;
 *   C1 — 90%: английский текст, редкие русские «аномалии».
 * Подсвеченные фразы-чанки до B2 включительно видны по-английски, на C1 —
 * по-русски (`displayIn`).
 * Доля проверяется схемой с допуском `ENGLISH_SHARE_TOLERANCE`.
 */

import { z } from 'zod';

import type { DrillDifficulty } from '../drillAbility';
import type { AdaptationState, CefrLevel, Strategy } from '../types';
import type { LevelMasteryView, NextStep } from '../mastery';
import { PhraseChunkSchema, phraseKey, type PhraseChunk, type PocketPhrase } from './phrase';
import { LanguageCodeSchema, type LanguageCode } from './language';

/* ── Ступени и последовательность ────────────────────────────────── */

/** Ступень программы: C2 в программе нет. */
export type CurriculumLevel = Exclude<CefrLevel, 'C2'>;

export const CURRICULUM_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'] as const satisfies readonly CurriculumLevel[];
export const CurriculumLevelSchema = z.enum(CURRICULUM_LEVELS);

/** Грамматических тем на ступень (CEFR, по порядку). */
export const TOPICS_PER_LEVEL = 30;
/**
 * Уроков на тему: знакомство, практика в новой ситуации, живой диалог и
 * повторение вместе с прошлыми темами. Одна тема — четыре разные истории.
 */
export const LESSONS_PER_TOPIC = 4;
/**
 * Потолок уроков на ступень: номер урока в id — три цифры. Сколько их на
 * самом деле, считает программа на сервере из банка фраз ступени.
 */
export const MAX_LESSONS_PER_LEVEL = 999;

/**
 * Ключ навыка: `A1-007` у английского курса (так он жил до многоязычности),
 * `de-A1-007` — у остальных. Он же id урока и `sourceLessonId` в кармане.
 */
export function skillId(level: CurriculumLevel, sequenceOrder: number, language: LanguageCode = 'en'): string {
  const base = `${level}-${String(sequenceOrder).padStart(3, '0')}`;
  return language === 'en' ? base : `${language}-${base}`;
}

/** Ключ навыка в URL и запросах: `A1-007` или `de-A1-007`. */
export const SKILL_ID_PATTERN = /^(?:(?:ru|zh|de|fr)-)?(?:A1|A2|B1|B2|C1)-\d{3}$/;

/** Изучаемый язык урока по ключу навыка: без префикса — английский. */
export function skillLanguage(id: string): LanguageCode {
  const prefix = /^(ru|zh|de|fr)-/.exec(id)?.[1];
  return prefix === 'ru' || prefix === 'zh' || prefix === 'de' || prefix === 'fr' ? prefix : 'en';
}

/* ── Затухающие подсказки ────────────────────────────────────────── */

/**
 * На какой стороне урока идёт текст истории и показаны чанки: на изучаемом
 * языке (`target`) или на языке перевода (`translation`). Для английского
 * курса русских учеников это прежние «en» и «ru».
 */
export type StoryLanguage = 'translation' | 'target';

export interface FadingRule {
  readonly narration: StoryLanguage;
  readonly chunksShownIn: StoryLanguage;
  /**
   * На какой стороне чанк виден и подсвечен в тексте. До B2 включительно —
   * фраза изучаемого языка (касанием — перевод): ученик учит язык, а не
   * переводит. Только на C1 — редкие «аномалии» на языке перевода, которые
   * нужно вспомнить на изучаемом. `chunksShownIn` остаётся контрактом
   * генерации и подсчёта доли: от него зависят уже сгенерированные уроки.
   */
  readonly displayIn: StoryLanguage;
  /** Сколько чанков-вкраплений допустимо в истории. */
  readonly minChunks: number;
  readonly maxChunks: number;
  /**
   * Сколько чанков урока N + 1 обязано повторять из уроков до него. На C1
   * вкраплений всего 1–3, поэтому повтор один — иначе новому не хватит места.
   */
  readonly minReviewChunks: number;
  /** Целевая доля слов изучаемого языка в истории (как её видит ученик), 0–1. */
  readonly targetShare: number;
}

/**
 * Допуск к целевой доле английского, ± доля. Модель не считает слова
 * точно: требование «ровно 20%» срывало бы почти каждую генерацию, а
 * коридор ±8 п. п. сохраняет плавную лестницу между ступенями.
 */
// Модель не умеет считать слова точно: узкий коридор (±8%) был главной
// причиной брака уроков. ±15% — смесь языков всё ещё та, что задумана.
export const TARGET_SHARE_TOLERANCE = 0.15;

export const FADING_RULES: Readonly<Record<CurriculumLevel, FadingRule>> = {
  A1: { narration: 'translation', chunksShownIn: 'target', displayIn: 'target', minChunks: 4, maxChunks: 8, minReviewChunks: 2, targetShare: 0.2 },
  A2: { narration: 'translation', chunksShownIn: 'target', displayIn: 'target', minChunks: 6, maxChunks: 12, minReviewChunks: 2, targetShare: 0.4 },
  B1: { narration: 'target', chunksShownIn: 'translation', displayIn: 'target', minChunks: 6, maxChunks: 12, minReviewChunks: 2, targetShare: 0.5 },
  // B2 и C1 расширены: литературный текст на узком коридоре (5–10, 1–3)
  // модель стабильно не попадала в счёт, и сид застревал на отдельных уроках.
  // Расширение совместимо: прежние уроки в кэше по-прежнему проходят схему.
  B2: { narration: 'target', chunksShownIn: 'translation', displayIn: 'target', minChunks: 4, maxChunks: 12, minReviewChunks: 2, targetShare: 0.7 },
  C1: { narration: 'target', chunksShownIn: 'translation', displayIn: 'translation', minChunks: 1, maxChunks: 5, minReviewChunks: 1, targetShare: 0.9 },
};

/** Допустимый коридор доли изучаемого языка на ступени. */
export function targetShareRange(level: CurriculumLevel): { readonly min: number; readonly max: number } {
  const target = FADING_RULES[level].targetShare;
  return { min: Math.max(0, target - TARGET_SHARE_TOLERANCE), max: Math.min(1, target + TARGET_SHARE_TOLERANCE) };
}

/* ── Длинная история ─────────────────────────────────────────────── */

export const STORY_GENRES = ['MYSTERY', 'SCI_FI', 'ADVENTURE', 'COMEDY', 'SLICE_OF_LIFE', 'HISTORICAL'] as const;
export const StoryGenreSchema = z.enum(STORY_GENRES);
export type StoryGenre = z.infer<typeof StoryGenreSchema>;

export const STORY_MIN_PARAGRAPHS = 5;
export const STORY_MAX_PARAGRAPHS = 8;

/** Плейсхолдер чанка в тексте абзаца: `{would-like}`. */
const CHUNK_PLACEHOLDER = /\{([a-z0-9-]{1,32})\}/g;

/** Идентификаторы чанков, на которые ссылается абзац, по порядку. */
export function chunkRefs(paragraph: string): readonly string[] {
  return Array.from(paragraph.matchAll(CHUNK_PLACEHOLDER), (match) => match[1] ?? '');
}

const WORD = /\p{L}+(?:['’]\p{L}+)*/gu;

type Script = 'latin' | 'cyrillic' | 'han';

const SCRIPT_OF: Readonly<Record<LanguageCode, Script>> = { en: 'latin', de: 'latin', fr: 'latin', ru: 'cyrillic', zh: 'han', es: 'latin' };

function scriptOfWord(word: string): Script | null {
  if (/[\u0400-\u04FF]/.test(word)) return 'cyrillic';
  if (/\p{Script=Han}/u.test(word)) return 'han';
  if (/\p{Script=Latin}/u.test(word)) return 'latin';
  return null;
}

/**
 * Доля слов изучаемого языка в истории так, как её видит ученик:
 * плейсхолдер чанка заменён тем, что показано на этой ступени
 * (`chunksShownIn`). Сторона слова определяется письменностью — латиница,
 * кириллица, иероглифы. У языков с общей письменностью (немецкий с
 * английским переводом) долю не измерить — `null`, проверка пропускается.
 */
export function storyTargetShare(
  story: { readonly paragraphs: readonly string[]; readonly chunks: readonly PhraseChunk[] },
  shownIn: StoryLanguage,
  targetLanguage: LanguageCode = 'en',
  translationLanguage: LanguageCode = 'ru',
): number | null {
  const targetScript = SCRIPT_OF[targetLanguage];
  const translationScript = SCRIPT_OF[translationLanguage];
  if (targetScript === translationScript) return null;
  const shown = new Map(story.chunks.map((chunk) => [chunk.id, shownIn === 'target' ? chunk.target : chunk.translation]));
  let target = 0;
  let translation = 0;
  for (const paragraph of story.paragraphs) {
    const text = paragraph.replace(CHUNK_PLACEHOLDER, (whole: string, id: string) => ` ${shown.get(id) ?? whole} `);
    for (const word of text.match(WORD) ?? []) {
      const script = scriptOfWord(word);
      if (script === targetScript) target += 1;
      else if (script === translationScript) translation += 1;
    }
  }
  return target + translation === 0 ? 0 : target / (target + translation);
}

/** Сколько символов контекста сохраняется в карман вокруг чанка. */
const CONTEXT_RADIUS = 160;

/**
 * Фрагмент истории вокруг первого вхождения чанка — контекст для кармана.
 * Плейсхолдеры раскрываются фразами изучаемого языка: в кармане чанк видно в
 * том виде, в каком его нужно говорить. `null` — чанка в тексте нет.
 */
export function chunkContext(
  story: { readonly paragraphs: readonly string[]; readonly chunks: readonly PhraseChunk[] },
  chunkId: string,
): string | null {
  const paragraph = story.paragraphs.find((text) => chunkRefs(text).includes(chunkId));
  if (paragraph === undefined) return null;

  const byId = new Map(story.chunks.map((chunk) => [chunk.id, chunk.target]));
  const marker = `{${chunkId}}`;
  const at = paragraph.indexOf(marker);
  const start = Math.max(0, at - CONTEXT_RADIUS);
  const end = Math.min(paragraph.length, at + marker.length + CONTEXT_RADIUS);
  const snippet = paragraph
    .slice(start, end)
    .replace(CHUNK_PLACEHOLDER, (whole: string, id: string) => byId.get(id) ?? whole)
    // Обрезанный по краю плейсхолдер не раскрылся — его остаток не нужен.
    .replace(/^[^{]*\}|\{[^}]*$/g, '')
    .trim();
  return `${start > 0 ? '…' : ''}${snippet}${end < paragraph.length ? '…' : ''}`;
}

/**
 * История урока. Абзацы написаны на языке повествования ступени, а
 * чанки вставлены плейсхолдерами `{id}`: на каком языке их показать,
 * решает рендер по `FADING_RULES`, а не модель.
 */
export const LongStorySchema = z.object({
  genre: StoryGenreSchema,
  /** Название на изучаемом языке. */
  titleTarget: z.string().trim().min(2).max(80),
  /** Название на языке перевода — его видит ученик. */
  title: z.string().trim().min(2).max(80),
  /** Крючок для карточки урока: одна интригующая строка на языке перевода. */
  hook: z.string().trim().min(10).max(160),
  /** Пересказ сюжета одним-двумя предложениями — для промпта следующего урока. */
  recap: z.string().trim().min(10).max(300),
  narration: z.enum(['translation', 'target']),
  // 100, а не 120: на A1 модель стабильно писала абзац в одну короткую
  // русскую фразу (~100 знаков), и жёсткий минимум браковал уже готовый
  // урок. Длину по ступеням задаёт промпт (`LEVEL_STORY`), схема — лишь
  // нижняя граница «это ещё абзац, а не строка».
  paragraphs: z
    .array(z.string().trim().min(100).max(1200))
    .min(STORY_MIN_PARAGRAPHS)
    .max(STORY_MAX_PARAGRAPHS),
  chunks: z.array(PhraseChunkSchema).min(1).max(12),
});
export type LongStory = z.infer<typeof LongStorySchema>;

/* ── Карточки правил ─────────────────────────────────────────────── */

/** Иконки карточек — ключи, которые UI сопоставляет со своим набором. */
export const RULE_ICONS = [
  'spark',
  'clock',
  'question',
  'negation',
  'link',
  'location',
  'person',
  'scale',
] as const;

/**
 * Карточка правила: формула и один пример вместо абзаца теории.
 * `formula` пишется слотами, как паттерны уроков: «I would like + [noun]».
 * `highlight` — фрагмент `example.target`, который UI подсвечивает.
 */
export const RuleCardSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9-]{1,32}$/),
    icon: z.enum(RULE_ICONS),
    title: z.string().trim().min(2).max(40),
    formula: z.string().trim().min(3).max(60),
    example: z.object({
      target: z.string().trim().min(2).max(120),
      translation: z.string().trim().min(2).max(120),
      // Фрагмент примера, а не отдельная строка: ограничен длиной самого
      // примера (120), иначе модель, подсветившая всё предложение, уходила
      // в запасной урок из-за формальности.
      highlight: z.string().trim().min(1).max(120),
    }),
    /** Необязательное уточнение — одна строка, не абзац. */
    note: z.string().trim().min(2).max(120).optional(),
    mistake: z
      .object({ wrong: z.string().trim().min(2).max(120), right: z.string().trim().min(2).max(120) })
      .optional(),
  })
  .refine((card) => card.example.target.includes(card.example.highlight), {
    message: 'highlight должен быть фрагментом example.target',
    path: ['example', 'highlight'],
  });
export type RuleCard = z.infer<typeof RuleCardSchema>;

/* ── Дриллы ──────────────────────────────────────────────────────── */

const DrillBase = {
  id: z.string().regex(/^[a-z0-9-]{1,32}$/),
  /** Чанк истории, который тренирует дрилл: из него берётся контекст кармана. */
  chunkId: z.string().regex(/^[a-z0-9-]{1,32}$/).optional(),
};

/**
 * Собрать фразу, перетаскивая слова. `tokens` — в верном порядке.
 * Два токена допустимы: короткие чанки A1 («wake up», «I'm sorry») и есть
 * весь материал такого дрилла — требование трёх слов браковало их.
 */
export const DragOrderDrillSchema = z.object({
  ...DrillBase,
  type: z.literal('DRAG_ORDER'),
  /** Что сделать — на языке перевода. */
  prompt: z.string().trim().min(2).max(160),
  tokens: z.array(z.string().trim().min(1).max(24)).min(2).max(10),
  /** Лишние слова в банке — чтобы порядок нельзя было угадать перебором. */
  distractors: z.array(z.string().trim().min(1).max(24)).max(3).default([]),
});

/** Произнести фразу вслух; проверка — по распознанной речи. */
export const FluencyDrillSchema = z.object({
  ...DrillBase,
  type: z.literal('FLUENCY'),
  phrase: z.string().trim().min(2).max(120),
  translation: z.string().trim().min(2).max(120),
  /** Другие допустимые формулировки (стяжения и т. п.). */
  acceptable: z.array(z.string().trim().min(2).max(120)).max(4).default([]),
});

/** Перевести чанк с языка перевода на изучаемый — главный дрилл ступеней B. */
export const TranslateChunkDrillSchema = z.object({
  ...DrillBase,
  type: z.literal('TRANSLATE_CHUNK'),
  translation: z.string().trim().min(2).max(120),
  answer: z.string().trim().min(2).max(120),
  acceptable: z.array(z.string().trim().min(2).max(120)).max(4).default([]),
});

/** Пропуск в предложении задания CHOICE. */
export const GAP = '___';
/** Хотя бы два знака (буквы, цифры) в предложении — контекст вокруг пропуска. */
const GAP_CONTEXT = /(?:[\p{L}\p{N}][^]*){2}/u;

/**
 * Части предложения вокруг пропуска — или `null`, если пропуска нет или
 * вокруг него нет слов (показывать такое задание нельзя: нечего вписывать).
 */
export function gapParts(sentence: string): { readonly before: string; readonly after: string } | null {
  const at = sentence.indexOf(GAP);
  if (at < 0 || !GAP_CONTEXT.test(sentence)) return null;
  return { before: sentence.slice(0, at), after: sentence.slice(at + GAP.length) };
}

/** «смысл» — ___ : пропуск с контекстом; длинный смысл укорачивается, а не пропуск. */
export function gapSentence(meaning: string): string {
  const room = 160 - '«» — ___'.length;
  const text = meaning.trim().length > room ? `${meaning.trim().slice(0, room - 1)}…` : meaning.trim();
  return `«${text}» — ${GAP}`;
}

/**
 * Выбрать форму для пропуска `___` в предложении. Что `answerIndex` не
 * выходит за `options`, проверяет урок: discriminatedUnion принимает
 * только чистые объекты, без `refine`.
 */
export const ChoiceDrillSchema = z.object({
  ...DrillBase,
  type: z.literal('CHOICE'),
  // Пропуск и хотя бы пара букв вокруг него: голый «___» не даёт ученику,
  // что вписывать, — экран задания выходил пустым.
  sentence: z.string().trim().min(5).max(160).includes(GAP).regex(GAP_CONTEXT, 'В предложении с пропуском нужен контекст, а не только ___'),
  /** Что сделать — на языке перевода. */
  prompt: z.string().trim().min(2).max(160),
  options: z.array(z.string().trim().min(1).max(40)).min(2).max(4),
  answerIndex: z.number().int().nonnegative(),
});

export const DrillSchema = z.discriminatedUnion('type', [
  DragOrderDrillSchema,
  FluencyDrillSchema,
  TranslateChunkDrillSchema,
  ChoiceDrillSchema,
]);
export type Drill = z.infer<typeof DrillSchema>;
export type DrillType = Drill['type'];

/** Каждый урок содержит дриллы всех видов. */
export const DRILL_TYPES = ['DRAG_ORDER', 'FLUENCY', 'TRANSLATE_CHUNK', 'CHOICE'] as const satisfies readonly DrillType[];

/* ── Прежние ключи ───────────────────────────────────────────────── */

/*
 * До многоязычности формат урока был английско-русским: `en` / `ru`,
 * `titleRu`, `promptRu`, повествование «ru» / «en». Так лежат уже
 * сгенерированные уроки и объяснения в базе, и так пока отвечает модель по
 * прежнему промпту. Схемы читают оба вида и всегда отдают новые ключи.
 */

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Копия объекта с переименованными ключами; новый ключ, если уже есть, побеждает. */
function renamed(value: unknown, keys: Readonly<Record<string, string>>): unknown {
  if (!isRecord(value)) return value;
  const result: JsonRecord = { ...value };
  for (const [from, to] of Object.entries(keys)) {
    if (from in result && !(to in result)) result[to] = result[from];
    delete result[from];
  }
  return result;
}

const LEGACY_SIDE: Readonly<Record<string, StoryLanguage>> = { ru: 'translation', en: 'target' };

function migrateLegacyStory(story: unknown): unknown {
  const next = renamed(story, { titleEn: 'titleTarget', titleRu: 'title', hookRu: 'hook', recapRu: 'recap' });
  if (!isRecord(next)) return next;
  const narration = typeof next.narration === 'string' ? (LEGACY_SIDE[next.narration] ?? next.narration) : next.narration;
  const chunks = Array.isArray(next.chunks) ? next.chunks.map((chunk) => renamed(chunk, { en: 'target', ru: 'translation' })) : next.chunks;
  return { ...next, narration, chunks };
}

function migrateLegacyLesson(raw: unknown): unknown {
  if (!isRecord(raw)) return raw;
  return {
    ...raw,
    story: migrateLegacyStory(raw.story),
    ruleCards: Array.isArray(raw.ruleCards)
      ? raw.ruleCards.map((card) => (isRecord(card) ? { ...card, example: renamed(card.example, { en: 'target', ru: 'translation' }) } : card))
      : raw.ruleCards,
    drills: Array.isArray(raw.drills) ? raw.drills.map((drill) => renamed(drill, { promptRu: 'prompt', ru: 'translation' })) : raw.drills,
  };
}

function migrateLegacyExplanation(raw: unknown): unknown {
  if (!isRecord(raw) || !Array.isArray(raw.examples)) return raw;
  return { ...raw, examples: raw.examples.map((example) => renamed(example, { en: 'target', ru: 'translation' })) };
}

/* ── Урок программы ──────────────────────────────────────────────── */

/**
 * Урок навыка целиком. Поверх формы проверяются инварианты, которые
 * модель склонна нарушать: язык повествования ступени, ссылки
 * плейсхолдеров, число вкраплений и индексы вариантов.
 */
export const CurriculumLessonSchema = z.preprocess(migrateLegacyLesson, z
  .object({
    skillId: z.string().regex(SKILL_ID_PATTERN),
    /** Изучаемый язык урока; уроки до многоязычности — английские. */
    targetLanguage: LanguageCodeSchema.default('en'),
    /** Язык перевода: на нём переводы фраз, задания и правила (до многоязычности — русский). */
    translationLanguage: LanguageCodeSchema.default('ru'),
    level: CurriculumLevelSchema,
    sequenceOrder: z.number().int().min(1).max(MAX_LESSONS_PER_LEVEL),
    grammarFocus: z.string().trim().min(2).max(80),
    story: LongStorySchema,
    ruleCards: z.array(RuleCardSchema).min(1).max(3),
    drills: z.array(DrillSchema).min(6).max(12),
  })
  .superRefine((lesson, ctx) => {
    const rule = FADING_RULES[lesson.level];
    const { story } = lesson;

    if (lesson.skillId !== skillId(lesson.level, lesson.sequenceOrder, lesson.targetLanguage)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['skillId'], message: 'skillId не совпадает с level и sequenceOrder' });
    }
    if (story.narration !== rule.narration) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['story', 'narration'], message: `На ${lesson.level} повествование идёт на «${rule.narration}»` });
    }
    if (story.chunks.length < rule.minChunks || story.chunks.length > rule.maxChunks) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['story', 'chunks'],
        message: `На ${lesson.level} нужно ${rule.minChunks}–${rule.maxChunks} чанков`,
      });
    }

    const known = new Set(story.chunks.map((chunk) => chunk.id));
    if (known.size !== story.chunks.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['story', 'chunks'], message: 'id чанков повторяются' });
    }

    const used = new Set<string>();
    story.paragraphs.forEach((paragraph, index) => {
      for (const ref of chunkRefs(paragraph)) {
        used.add(ref);
        if (!known.has(ref)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['story', 'paragraphs', index], message: `Неизвестный чанк {${ref}}` });
        }
      }
    });

    // Смесь языков: доля изучаемого языка в коридоре ступени.
    const share = storyTargetShare(story, rule.chunksShownIn, lesson.targetLanguage, lesson.translationLanguage);
    const { min, max } = targetShareRange(lesson.level);
    if (share !== null && (share < min || share > max)) {
      // Подсказка задаёт структуру, а не процент: модель устойчиво
      // перелетает коридор, когда просишь «добавь языка» без объёма
      // (90% → 17% → 91%), а готовый цикл предложений держит долю.
      const current = Math.round(share * 100);
      const goal = Math.round(rule.targetShare * 100);
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['story', 'paragraphs'],
        message:
          share < min
            ? `Слов изучаемого языка ${current}%, на ${lesson.level} нужно ${Math.round(min * 100)}–${Math.round(max * 100)}% (цель ${goal}%). В каждый абзац добавь 2–3 полных предложения на изучаемом языке.`
            : `Слов изучаемого языка ${current}%, на ${lesson.level} нужно ${Math.round(min * 100)}–${Math.round(max * 100)}% (цель ${goal}%). В каждом абзаце после каждых двух английских предложений вставь одно полное русское той же длины — размышление рассказчика или мысль героя; не переводи дословно.`,
      });
    }
    for (const chunk of story.chunks) {
      if (!used.has(chunk.id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['story', 'chunks'], message: `Чанк «${chunk.id}» не встречается в тексте` });
      }
    }

    for (const type of DRILL_TYPES) {
      if (!lesson.drills.some((drill) => drill.type === type)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['drills'], message: `Нет ни одного дрилла ${type}` });
      }
    }

    lesson.drills.forEach((drill, index) => {
      if (drill.chunkId !== undefined && !known.has(drill.chunkId)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['drills', index, 'chunkId'], message: `Неизвестный чанк «${drill.chunkId}»` });
      }
      if (drill.type === 'CHOICE' && drill.answerIndex >= drill.options.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['drills', index, 'answerIndex'], message: 'answerIndex вне списка вариантов' });
      }
    });
  }));
export type CurriculumLesson = z.infer<typeof CurriculumLessonSchema>;

/**
 * Проверка последовательности: урок N + 1 опирается на уроки до него.
 * Каждый чанк с `isNew: false` обязан встречаться раньше, и таких чанков
 * не меньше `MIN_REVIEW_CHUNKS`. Возвращает список нарушений — пустой
 * список значит, что урок вписывается в программу. Первому уроку
 * ступени повторять нечего.
 */
export function continuityIssues(
  lesson: CurriculumLesson,
  earlierChunks: readonly PhraseChunk[],
): readonly string[] {
  if (lesson.sequenceOrder === 1) return [];

  const earlier = new Set(earlierChunks.map((chunk) => phraseKey(chunk.target)));
  const reviewed = lesson.story.chunks.filter((chunk) => !chunk.isNew);
  const required = FADING_RULES[lesson.level].minReviewChunks;
  const issues: string[] = [];

  for (const chunk of reviewed) {
    if (!earlier.has(phraseKey(chunk.target))) {
      issues.push(`Чанк «${chunk.target}» помечен как повтор, но в прошлых уроках его нет`);
    }
  }
  if (reviewed.length < required) {
    issues.push(`Урок повторяет ${reviewed.length} чанков из прошлых уроков, нужно не меньше ${required}`);
  }
  // Урок из одних повторов ничему новому не учит.
  if (reviewed.length === lesson.story.chunks.length) {
    issues.push('В уроке нет ни одного нового чанка');
  }
  return issues;
}

/* ── Прохождение урока ───────────────────────────────────────────── */

/** Ответ на один дрилл: засчитывается первая попытка. */
export const DrillAnswerSchema = z.object({
  drillId: z.string().min(1).max(64),
  /** Верно ли с первой попытки: дрилл засчитывается один раз. */
  correct: z.boolean(),
  /** Сколько думал над заданием, мс (до 10 минут); старые клиенты не шлют. */
  timeMs: z.number().int().nonnegative().max(600_000).optional(),
  /**
   * Уровень варианта, в котором задание показали (`DrillDifficulty`): −1
   * легче, 0 как в уроке, +1 труднее. По нему сервер обновляет способность
   * ученика к виду заданий; старые клиенты не шлют — значит, 0.
   */
  difficulty: z.union([z.literal(-1), z.literal(0), z.literal(1)]).optional(),
});
export type DrillAnswer = z.infer<typeof DrillAnswerSchema>;

export const CompleteCurriculumLessonSchema = z.object({
  userId: z.string().uuid(),
  /** Маршрут, по которому шёл урок: история до правил или после. */
  strategy: z.enum(['INDUCTIVE', 'DEDUCTIVE']),
  timeSpentMs: z.number().int().nonnegative(),
  answers: z.array(DrillAnswerSchema).min(1).max(32),
});
export type CompleteCurriculumLesson = z.infer<typeof CompleteCurriculumLessonSchema>;

/** Доля верных первых попыток, с которой урок считается пройденным чисто. */
export const LESSON_CLEAN_RATIO = 0.8;

/* ── Представления для клиента ───────────────────────────────────── */

export type SkillStatus = 'locked' | 'available' | 'completed';

/** Узел карты: навык программы и его состояние у ученика. */
export interface SkillNodeView {
  readonly skillId: string;
  readonly level: CurriculumLevel;
  readonly sequenceOrder: number;
  readonly title: string;
  readonly grammarFocus: string;
  readonly status: SkillStatus;
  /** BKT `pKnown`; `null` — навык ещё не начинали. */
  readonly mastery: number | null;
}

/**
 * Ступень на карте. `passed` — ниже текущей (уроки открыты для повтора),
 * `current` — проходится линейно, `locked` — выше текущей.
 */
export interface CurriculumLevelView {
  readonly level: CurriculumLevel;
  readonly status: 'passed' | 'current' | 'locked';
  readonly skills: readonly SkillNodeView[];
}

export interface CurriculumMapView {
  readonly language: LanguageCode;
  /** Программы для языка ещё нет: `levels` пусты, уроки появятся позже. */
  readonly contentReady: boolean;
  readonly currentLevel: CurriculumLevel;
  readonly levels: readonly CurriculumLevelView[];
  /** Освоенность текущей ступени; `null` — программы ещё нет. */
  readonly mastery: LevelMasteryView | null;
  /** Что делать дальше (планировщик); `null` — программы ещё нет. */
  readonly next: NextStep | null;
}

/** Урок для показа: `strategy` задаёт порядок — история или правила первыми. */
/**
 * Ответ на «подготовь следующий урок»: сервер начал его готовить
 * (`preparing`), он уже в кэше (`ready`) или следующего урока нет (`none`).
 * Содержимое урока не отдаётся — он ещё закрыт.
 */
export interface PrefetchNextLessonResult {
  readonly nextSkillId: string | null;
  readonly status: 'ready' | 'preparing' | 'none';
}

export interface CurriculumLessonView {
  /**
   * Урок под этого ученика: дриллы — по его нагрузке (`vocabLoad`), плюс
   * задания на повтор фраз, которые он вот-вот забудет (FSRS), и при
   * EXPANDED — бонусные фразы посложнее из банка фраз.
   */
  readonly lesson: CurriculumLesson;
  readonly title: string;
  readonly strategy: Strategy;
  /** Ведомый режим: дриллы маленькими порциями и автоозвучка квокки. */
  readonly guided: boolean;
  /** Фразы повторов и бонусов: у их дриллов `chunkId` ссылается сюда (картинка). */
  readonly extraChunks: readonly PhraseChunk[];
  /** Поддержка квокки перед заданиями: говорение даётся трудно. */
  readonly support: 'speaking' | null;
  /**
   * Разбор последних уроков решил, что ученику трудно: перед заданиями
   * сразу показать объяснение «проще».
   */
  readonly autoSimplify: boolean;
  /**
   * С какого уровня начать каждый вид заданий (`drillAbility`): там, где
   * ученик в прошлых уроках держал около 80% верных. Вида нет — «как в уроке».
   */
  readonly startDifficulties: Partial<Record<DrillType, DrillDifficulty>>;
}

export interface CompleteCurriculumLessonResult {
  readonly skillId: string;
  readonly mastery: number;
  readonly mastered: boolean;
  /** Не меньше `LESSON_CLEAN_RATIO` верных первых попыток. */
  readonly clean: boolean;
  readonly adaptation: AdaptationState;
  readonly currentLevel: CurriculumLevel;
  /** Ступень освоена, и ученик перешёл на следующую. */
  readonly levelUp: boolean;
  /** Следующий урок, если следующий шаг — урок; иначе `null`. */
  readonly nextSkillId: string | null;
  readonly addedPhrases: readonly PocketPhrase[];
  /** Что делать дальше: урок, закрепление, проверка знаний или финиш. */
  readonly next: NextStep;
  /** Освоенность ступени после урока. */
  readonly levelMastery: LevelMasteryView;
}

/**
 * «Попробовать проще»: тема урока объяснена лайфхаками, аналогией и самым
 * простым языком, плюс три лёгких примера. Одно объяснение на урок.
 */
export const SimplifiedExplanationSchema = z.preprocess(migrateLegacyExplanation, z.object({
  /** Суть темы в двух-трёх простых предложениях. */
  summary: z.string().trim().min(10).max(600),
  /** Аналогия из жизни, на которую тема «ложится». */
  analogy: z.string().trim().min(10).max(400),
  hacks: z
    .array(z.object({ title: z.string().trim().min(2).max(60), text: z.string().trim().min(5).max(240) }))
    .min(1)
    .max(4),
  examples: z
    .array(z.object({ target: z.string().trim().min(2).max(120), translation: z.string().trim().min(2).max(160), note: z.string().trim().max(160).optional() }))
    .length(3),
}));
export type SimplifiedExplanation = z.infer<typeof SimplifiedExplanationSchema>;

/** «Сомневаюсь»: задание, на котором ученик застрял. */
export const DrillContextSchema = z.object({
  drillId: z.string().trim().min(1).max(80),
  /** Что просит задание — как его видит ученик. */
  instruction: z.string().trim().min(1).max(400),
  /** Целевая фраза задания, если есть. */
  phrase: z.string().trim().max(200).optional(),
  /** Верный ответ. */
  answer: z.string().trim().min(1).max(300),
});
export type DrillContext = z.infer<typeof DrillContextSchema>;

/** Подсказка к конкретному заданию: как решить, лайфхак и верный ответ. */
export const DrillHintSchema = z.object({
  explanation: z.string().trim().min(10).max(600),
  hack: z.string().trim().min(5).max(300),
  answer: z.string().trim().min(1).max(300),
});
export type DrillHint = z.infer<typeof DrillHintSchema>;
